import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useVenue } from "../../shell/VenueContext";

/**
 * The Settings hub landing ("Général") — a read-only summary of the venue's identity (name, public
 * slug, time zone, language, lifecycle). It gives the hub a home page; editing individual facets
 * (hours, site, address) lives in the dedicated sub-sections. Wiring the inline address editor
 * (features/venue-location) here is a follow-up.
 */
/** Localised display name for an ISO language code (`fr` → "Français" / "French"), or null if the
 *  platform can't resolve it — the caller then falls back to the raw code. */
function languageName(code: string, uiLocale: string): string | null {
  try {
    return new Intl.DisplayNames([uiLocale], { type: "language" }).of(code) ?? null;
  } catch {
    return null;
  }
}

export function SettingsGeneral() {
  const { venue } = useVenue();
  const { t, i18n } = useTranslation();

  // Humanise the two developer-facing enums a restaurateur shouldn't have to decode: the ISO language
  // code (`fr` → "Français") and the internal lifecycle state (`READY` → "Prêt à ouvrir"). Both fall
  // back to the raw value for anything not yet translated, so an unknown state never renders blank.
  const language =
    languageName(venue.defaultLanguage, i18n.language) ?? venue.defaultLanguage.toUpperCase();
  const lifecycle = t(`shell.settings.general.lifecycleValue.${venue.lifecycle}`, {
    defaultValue: venue.lifecycle,
  });

  const rows: Array<{ label: string; value: string }> = [
    { label: t("shell.settings.general.name"), value: venue.displayName },
    { label: t("shell.settings.general.slug"), value: venue.slug },
    { label: t("shell.settings.general.timeZone"), value: venue.timeZone },
    { label: t("shell.settings.general.language"), value: language },
    { label: t("shell.settings.general.lifecycle"), value: lifecycle },
  ];

  return (
    <section className={css({ display: "flex", flexDirection: "column", gap: "4", maxW: "2xl" })}>
      <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
        <h2 className={css({ fontSize: "lg", fontWeight: "semibold", color: "fg.default" })}>
          {t("shell.settings.general")}
        </h2>
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>
          {t("shell.settings.general.intro")}
        </p>
      </div>
      <dl
        className={css({
          display: "grid",
          gridTemplateColumns: { base: "1fr", sm: "auto 1fr" },
          columnGap: "6",
          rowGap: "3",
          borderWidth: "1px",
          borderColor: "border.default",
          rounded: "lg",
          bg: "bg.default",
          p: "5",
        })}
      >
        {rows.map((row) => (
          <div key={row.label} className={css({ display: "contents" })}>
            <dt className={css({ fontSize: "sm", color: "fg.muted", fontWeight: "medium" })}>
              {row.label}
            </dt>
            <dd className={css({ fontSize: "sm", color: "fg.default" })}>{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
